// Menu dropdown handlers (Layer, Filter, Select, View, Window, Edit, Image, Type, 3D, Help, Arrange)
import type { EditorState } from "./state/EditorState";
import { findLayer } from "../core/document/LayerUtils";
import type { FreeTransformMode, FreeTransformTool } from "../core/tools/FreeTransformTool";
import type { ToolContext } from "../core/tools/Tool";
import type { OrientationOp } from "../core/pixel/orientationTransform";
import type { AlignOp } from "../core/commands/AlignLayersCommand";
import type { DistributeOp } from "../core/commands/DistributeLayersCommand";

export interface MenuDeps {
  state: EditorState;
  // audit i23/i24: the live FreeTransformTool instance + a ToolContext
  // factory so the Edit menu can actually begin()/mode-switch it, and a
  // layer-scoped (not whole-canvas) flip/rotate for Edit > Transform.
  freeTransformTool: FreeTransformTool;
  toolCtx: () => ToolContext;
  highlightToolByShortcut: (shortcut: string) => void;
  transformLayerOrientation: (op: OrientationOp) => void;
  showMenuDropdown: (anchor: HTMLElement, items: ReadonlyArray<{ label: string; shortcut?: string; disabled?: boolean; action?: () => void; separator?: boolean }>) => void;
  selectAll: () => void;
  deselect: () => void;
  reselect: () => void;
  selectInverse: () => void;
  transformSelection: () => void;
  saveSelection: () => void;
  loadSelection: () => void;
  colorRange: () => void;
  selectAndMask: () => void;
  selectBorder: () => void;
  selectSmooth: () => void;
  selectExpand: () => void;
  selectContract: () => void;
  selectFeather: () => void;
  selectGrow: () => void;
  selectSimilar: () => void;
  colorRangeEnhanced: () => void;
  makeWorkPath: () => void;
  newDocument: () => void;
  closeDoc: () => void;
  closeAllDocs: () => void;
  showSaveAs: () => void;
  quickExportPng: () => void;
  exportAs: () => void;
  revertDocument: () => void;
  placeEmbedded: () => void;
  openAsSmartObject: () => void;
  showFileInfo: () => void;
  showPrintDialog: () => void;
  saveForWeb: () => void;
  fileInput: HTMLInputElement;
  getRecentFiles: () => string[];
  showRecentFilesMenu: (anchor: HTMLElement, files: string[]) => void;
  cutSelection: () => void;
  copySelection: () => void;
  copyMergedSelection: () => void;
  pasteClipboard: () => void;
  pasteInPlace: () => void;
  pasteInto: () => void;
  pasteOutside: () => void;
  clearSelection: () => void;
  promptFill: () => void;
  promptStroke: () => void;
  promptContentAwareFill: () => void;
  defineBrushPreset: () => void;
  definePattern: () => void;
  promptPurge: () => void;
  transformAgain: () => void;
  promptColorSettings: () => void;
  openShortcutsEditor: () => void;
  promptPreferences: () => void;
  findAndReplaceText: () => void;
  checkSpelling: () => void;
  convertToGrayscale: () => void;
  showImageAdjustmentsMenu: () => void;
  promptImageSize: () => void;
  promptCanvasSize: () => void;
  rotateImage: (mode: string) => void;
  promptArbitraryRotation: () => void;
  trimImage: () => void;
  revealAll: () => void;
  promptNewLayer: () => void;
  showFillLayerMenu: () => void;
  showAdjustmentLayerMenu: () => void;
  addNewArtboard: () => void;
  promptDuplicateLayer: () => void;
  deleteActiveLayer: () => void;
  layerViaCopy: () => void;
  layerViaCut: () => void;
  convertToSmartObject: () => void;
  editSmartObject: () => void;
  replaceSmartObjectContents: () => void;
  exportSmartObject: () => void;
  rasterizeSmartObject: () => void;
  createClippingMask: () => void;
  releaseClippingMask: () => void;
  groupActive: () => void;
  ungroupActive: () => void;
  mergeDownActive: () => void;
  mergeSelectedLayers: () => void;
  alignLayers: (op: AlignOp) => void;
  distributeLayers: (op: DistributeOp) => void;
  /** Current Align/Distribute target-layer ids (active ∪ selected) — used to
   * gray out the Align/Distribute menu items when there's nothing to act on. */
  alignTargetIds: () => string[];
  /** i125: Layer > Arrange (Bring to Front/Forward, Send Backward/to Back). */
  reorderActiveLayer: (direction: "front" | "forward" | "backward" | "back") => void;
  /** i128: F6 / Window > Color — mount + sync the real ColorPanel instance. */
  openColorPanel: () => void;
  /** i84: Window > Clone Source — open the real (if still stubby) panel. */
  openCloneSourcePanel: () => void;
  flattenImage: () => void;
  groupFromLayers: () => void;
  duplicateToNewDocument: () => void;
  exportLayersToFiles: () => void;
  exportArtboardsToFiles: () => void;
  exportArtboardsToPdf: () => void;
  exportPathsToIllustrator: () => void;
  exportColorLookupTables: () => void;
  placeLinked: () => void;
  conditionalModeChange: () => void;
  createDroplet: () => void;
  viewPrintSize: () => void;
  toggleSlices: () => void;
  showLayerStyleDialog: () => void;
  showWarpTextDialog: () => void;
  promptApplyImage: () => void;
  promptCalculations: () => void;
  rasterizeShapeLayer: () => void;
  copyLayerStyle: () => void;
  pasteLayerStyle: () => void;
  clearLayerStyle: () => void;
  addLayerStyleEffect: (effect: string) => void;
  promptBlendIf: () => void;
  layerProperties: () => void;
  blendingOptions: () => void;
  scaleLayerEffects: () => void;
  setGlobalLight: () => void;
  zoomCentered: (factor: number) => void;
  fitDoc: () => void;
  resetTo100: () => void;
  zoomTo200: () => void;
  toggleRulers: () => void;
  toggleGrid: () => void;
  promptGridSettings: () => void;
  toggleGuides: () => void;
  toggleExtras: () => void;
  toggleSnap: () => void;
  toggleSnapToGuides: () => void;
  toggleSnapToGrid: () => void;
  toggleSnapToLayers: () => void;
  toggleSnapToDocumentBounds: () => void;
  newGuide: () => void;
  newGuideLayout: () => void;
  clearGuides: () => void;
  toggleLockGuides: () => void;
  togglePixelGrid: () => void;
  toggleLayerEdges: () => void;
  cycleScreenMode: () => void;
  getCurrentWorkspace: () => string;
  buildWorkspaceMenuItems: (anchor: HTMLElement) => any[];
  openNavigatorPanel: () => void;
  openInfoPanel: () => void;
  openHistogramPanel: () => void;
  openSwatchesPanel: () => void;
  openBrushSettingsPanel: () => void;
  openGradientEditor: () => void;
  openPatternEditor: () => void;
  openStylesPanel: () => void;
  openPropertiesPanel: () => void;
  openAdjustmentsPanel: () => void;
  openShapesPanel: () => void;
  showLayerCompsPanel: () => void;
  tileDocuments: () => void;
  cascadeDocuments: () => void;
  floatDocument: () => void;
  consolidateTabs: () => void;
  showActionsPanel: () => void;
  showBatchDialog: () => void;
  showImageProcessor: () => void;
  showScriptsDialog: () => void;
  openTutorials: () => void;
  showWhatsNew: () => void;
  showAbout: () => void;
  repeatLastFilter: () => void;
  isSmartObjectActive: () => boolean;
  smartBoxBlur: () => void;
  smartGaussianBlur: () => void;
  smartMotionBlur: () => void;
  smartSharpen: () => void;
  smartSharpenMore: () => void;
  smartUnsharpMask: () => void;
  smartAddNoise: () => void;
  smartMedian: () => void;
  smartDustScratches: () => void;
  smartMosaic: () => void;
  smartTwirl: () => void;
  smartPinch: () => void;
  smartSpherize: () => void;
  smartFindEdges: () => void;
  smartEmboss: () => void;
  smartHighPass: () => void;
  promptAutoTone: () => void;
  promptAutoContrast: () => void;
  promptAutoColor: () => void;
  promptColorBalance: () => void;
  promptBlackWhite: () => void;
  promptChannelMixer: () => void;
  promptColorLookup: () => void;
  promptExposure: () => void;
  promptSelectiveColor: () => void;
  promptShadowsHighlights: () => void;
  promptGradientMap: () => void;
  promptHueSaturation: () => void;
  promptInvert: () => void;
  promptPhotoFilter: () => void;
  promptPosterize: () => void;
  promptThreshold: () => void;
  promptVibrance: () => void;
  promptBoxBlur: () => void;
  promptGaussianBlur: () => void;
  promptMotionBlur: () => void;
  promptSharpen: () => void;
  promptSharpenMore: () => void;
  promptUnsharpMask: () => void;
  promptAddNoise: () => void;
  promptMedian: () => void;
  promptDustScratches: () => void;
  promptMosaic: () => void;
  promptTwirl: () => void;
  promptPinch: () => void;
  promptSpherize: () => void;
  promptFindEdges: () => void;
  promptEmboss: () => void;
  promptHighPass: () => void;
  promptClouds: () => void;
  promptDiffClouds: () => void;
  promptFibers: () => void;
  promptLensFlare: () => void;
  promptLightingEffects: () => void;
  promptDisplace: () => void;
  promptRipple: () => void;
  promptShear: () => void;
  promptWave: () => void;
  promptZigZag: () => void;
  promptPolarCoordinates: () => void;
  promptAverageBlur: () => void;
  promptSurfaceBlur: () => void;
  promptSmartBlur: () => void;
  promptLensBlur: () => void;
  promptRadialBlur: () => void;
  promptShapeBlur: () => void;
  promptFieldBlur: () => void;
  promptIrisBlur: () => void;
  promptTiltShift: () => void;
  promptCrystallize: () => void;
  promptFacet: () => void;
  promptFragment: () => void;
  promptMezzotint: () => void;
  promptPointillize: () => void;
  promptSolarize: () => void;
  promptTiles: () => void;
  promptTraceContour: () => void;
  promptWind: () => void;
  promptGlowingEdges: () => void;
  promptDiffuse: () => void;
  promptExtrude: () => void;
  promptPictureFrame: () => void;
  promptBrightnessContrast: () => void;
  promptOffset: () => void;
  promptMaximum: () => void;
  promptMinimum: () => void;
  promptHSBHSL: () => void;
  promptReduceNoise: () => void;
  promptSmartSharpen: () => void;
  promptOilPaint: () => void;
  filterGallery: () => void;
  lensCorrection: () => void;
  adaptiveWideAngle: () => void;
  duplicateDocument: () => void;
  selectAllLayers: () => void;
  deselectLayers: () => void;
  findLayers: () => void;
  hideLayers: () => void;
  showAllLayers: () => void;
  hideOthers: () => void;
  toggleSelectionEdges: () => void;
  toggleTargetPath: () => void;
  toggleNotes: () => void;
  showSelectionEdges: boolean;
  showTargetPath: boolean;
  showNotes: boolean;
  desaturateImage: () => void;
  promptLevels: () => void;
  promptCurves: () => void;
  proofColorsEnabled: boolean;
  gamutWarningEnabled: boolean;
  showRulers: boolean;
  showGrid: boolean;
  showGuides: boolean;
  showSmartGuides: boolean;
  showExtras: boolean;
  snapEnabled: boolean;
  snapToGuides: boolean;
  snapToGrid: boolean;
  snapToLayers: boolean;
  snapToDocumentBounds: boolean;
  guidesLocked: boolean;
  screenMode: string;
}

/**
 * Ensure the menubar contains the Type and 3D top-level menus.
 * The base HTML only ships File/Edit/Image/Layer/Select/Filter/View/Window/Help;
 * Photoshop places Type between Layer and Select, and 3D between Filter and View.
 * We inject the missing <li>s here so the rest of the codebase can stay HTML-stable.
 */
function ensureTopLevelMenus(): void {
  const ul = document.querySelector<HTMLUListElement>(".menubar__items");
  if (!ul) return;
  const lis = Array.from(ul.querySelectorAll<HTMLLIElement>("li"));
  const has = (name: string) => lis.some((l) => l.textContent?.trim() === name);

  const insertAfter = (afterName: string, newName: string) => {
    if (has(newName)) return;
    const refs = Array.from(ul.querySelectorAll<HTMLLIElement>("li"));
    const ref = refs.find((l) => l.textContent?.trim() === afterName);
    const li = document.createElement("li");
    li.textContent = newName;
    if (ref && ref.nextSibling) ul.insertBefore(li, ref.nextSibling);
    else if (ref) ul.appendChild(li);
    else ul.appendChild(li);
  };

  // Type goes between Layer and Select
  insertAfter("Layer", "Type");
  // 3D goes between Filter and View
  insertAfter("Filter", "3D");
}

export function wireMenus(deps: MenuDeps): void {
  ensureTopLevelMenus();
  const showTip = (msg: string) => {
    const fn = (deps as any).showTip;
    if (typeof fn === "function") fn(msg);
  };
  const comingSoon = (name: string) => () => showTip(`${name}: Coming soon`);

  // #100: clone each menubar <li> to strip any previously-attached click
  // listeners (e.g. the legacy inline wiring in main.ts that was triggering
  // the New Document dialog when the Layer menubar item was clicked). After
  // this step the only handlers are the ones we attach below.
  const menubarUl = document.querySelector<HTMLUListElement>(".menubar__items");
  if (menubarUl) {
    Array.from(menubarUl.querySelectorAll<HTMLLIElement>("li")).forEach((oldLi) => {
      const fresh = oldLi.cloneNode(true) as HTMLLIElement;
      oldLi.replaceWith(fresh);
    });
  }

  document.querySelectorAll<HTMLLIElement>(".menubar__items li").forEach((li) => {
    // #100: every menubar click must stop here. Without this, the document-
    // level keydown / click handlers can fire side-effects (e.g. opening
    // New Document) on the same click event.
    const stopLeaks = (e: Event) => { e.stopPropagation(); e.preventDefault(); };
    li.addEventListener("mousedown", stopLeaks);
    li.addEventListener("pointerdown", stopLeaks);
    if (li.textContent === "Select") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        // #127: Modify is a submenu in PS, Subject/Sky/Focus Area added,
        // "Color Range Enhanced" removed (not a PS item), All Layers moved
        // near the top with Alt+Ctrl+A, Find Layers shortcut corrected to
        // Alt+Shift+Ctrl+F, Feather gets Shift+F6, Select and Mask gets
        // Alt+Ctrl+R, Quick Mask Mode (Q) added.
        deps.showMenuDropdown(li, [
          { label: "All", shortcut: "Ctrl+A", action: deps.selectAll },
          { label: "Deselect", shortcut: "Ctrl+D", action: deps.deselect },
          { label: "Reselect", shortcut: "Ctrl+Shift+D", action: deps.reselect },
          { label: "Inverse", shortcut: "Ctrl+Shift+I", action: deps.selectInverse },
          { separator: true, label: "" },
          { label: "All Layers", shortcut: "Alt+Ctrl+A", action: () => deps.selectAllLayers() },
          { label: "Deselect Layers", action: () => deps.deselectLayers() },
          { label: "Find Layers…", shortcut: "Alt+Shift+Ctrl+F", action: () => deps.findLayers() },
          { label: "Isolate Layers", action: comingSoon("Isolate Layers") },
          { separator: true, label: "" },
          { label: "Color Range…", action: () => deps.colorRange() },
          { label: "Focus Area…", action: comingSoon("Focus Area") },
          { label: "Subject", action: comingSoon("Select Subject") },
          { label: "Sky", action: comingSoon("Select Sky") },
          { separator: true, label: "" },
          { label: "Select and Mask…", shortcut: "Alt+Ctrl+R", action: () => deps.selectAndMask() },
          { label: "Modify", action: () => {
            deps.showMenuDropdown(li, [
              { label: "Border…", action: () => deps.selectBorder() },
              { label: "Smooth…", action: () => deps.selectSmooth() },
              { label: "Expand…", action: () => deps.selectExpand() },
              { label: "Contract…", action: () => deps.selectContract() },
              { label: "Feather…", shortcut: "Shift+F6", action: () => deps.selectFeather() },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Grow", action: () => deps.selectGrow() },
          { label: "Similar", action: () => deps.selectSimilar() },
          { separator: true, label: "" },
          { label: "Transform Selection", action: deps.transformSelection },
          { separator: true, label: "" },
          { label: "Edit in Quick Mask Mode", shortcut: "Q", action: () => (deps as any).toggleQuickMask?.() ?? comingSoon("Quick Mask Mode")() },
          { separator: true, label: "" },
          { label: "Save Selection…", action: () => deps.saveSelection() },
          { label: "Load Selection…", action: () => deps.loadSelection() },
          { separator: true, label: "" },
          { label: "Make Work Path…", action: () => deps.makeWorkPath() },
        ]);
      });
    }

    if (li.textContent === "Filter") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        const isSmartObj = deps.isSmartObjectActive();
        const sfSuffix = isSmartObj ? " (Smart Filter)" : "";
        // #61: anchor submenus to THIS menubar item, not the first li.
        const subAnchor = () => li;
        deps.showMenuDropdown(li, [
          { label: "Last Filter", shortcut: "Ctrl+F", disabled: !(deps as any).lastFilter, action: deps.repeatLastFilter },
          { label: "Convert for Smart Filters", action: () => deps.convertToSmartObject() },
          { separator: true, label: "" },
          { label: "Filter Gallery…", action: () => deps.filterGallery() },
          { label: "Adaptive Wide Angle…", shortcut: "Alt+Shift+Ctrl+A", action: () => deps.adaptiveWideAngle() },
          { label: "Camera Raw Filter…", shortcut: "Shift+Ctrl+A", action: comingSoon("Camera Raw Filter") },
          { label: "Lens Correction…", shortcut: "Shift+Ctrl+R", action: () => deps.lensCorrection() },
          { label: "Liquify…", shortcut: "Shift+Ctrl+X", action: comingSoon("Liquify") },
          { label: "Vanishing Point…", shortcut: "Alt+Ctrl+V", action: comingSoon("Vanishing Point") },
          { separator: true, label: "" },
          { label: "Blur", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Average" + sfSuffix, action: () => deps.promptAverageBlur() },
              { label: "Box Blur…" + sfSuffix, action: isSmartObj ? () => deps.smartBoxBlur() : () => deps.promptBoxBlur() },
              { label: "Gaussian Blur…" + sfSuffix, shortcut: "Ctrl+Shift+B", action: isSmartObj ? () => deps.smartGaussianBlur() : () => deps.promptGaussianBlur() },
              { label: "Lens Blur…" + sfSuffix, action: () => deps.promptLensBlur() },
              { label: "Motion Blur…" + sfSuffix, action: isSmartObj ? () => deps.smartMotionBlur() : () => deps.promptMotionBlur() },
              { label: "Radial Blur…" + sfSuffix, action: () => deps.promptRadialBlur() },
              { label: "Shape Blur…" + sfSuffix, action: () => deps.promptShapeBlur() },
              { label: "Smart Blur…" + sfSuffix, action: () => deps.promptSmartBlur() },
              { label: "Surface Blur…" + sfSuffix, action: () => deps.promptSurfaceBlur() },
            ]);
          }},
          { label: "Blur Gallery", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Field Blur…" + sfSuffix, action: () => deps.promptFieldBlur() },
              { label: "Iris Blur…" + sfSuffix, action: () => deps.promptIrisBlur() },
              { label: "Tilt-Shift…" + sfSuffix, action: () => deps.promptTiltShift() },
            ]);
          }},
          { label: "Distort", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Displace…" + sfSuffix, action: () => deps.promptDisplace() },
              { label: "Pinch…" + sfSuffix, action: isSmartObj ? () => deps.smartPinch() : () => deps.promptPinch() },
              { label: "Polar Coordinates…" + sfSuffix, action: () => deps.promptPolarCoordinates() },
              { label: "Ripple…" + sfSuffix, action: () => deps.promptRipple() },
              { label: "Shear…" + sfSuffix, action: () => deps.promptShear() },
              { label: "Spherize…" + sfSuffix, action: isSmartObj ? () => deps.smartSpherize() : () => deps.promptSpherize() },
              { label: "Twirl…" + sfSuffix, action: isSmartObj ? () => deps.smartTwirl() : () => deps.promptTwirl() },
              { label: "Wave…" + sfSuffix, action: () => deps.promptWave() },
              { label: "ZigZag…" + sfSuffix, action: () => deps.promptZigZag() },
            ]);
          }},
          { label: "Noise", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Add Noise…" + sfSuffix, action: isSmartObj ? () => deps.smartAddNoise() : () => deps.promptAddNoise() },
              { label: "Despeckle" + sfSuffix, action: comingSoon("Despeckle") },
              { label: "Dust & Scratches…" + sfSuffix, action: isSmartObj ? () => deps.smartDustScratches() : () => deps.promptDustScratches() },
              { label: "Median…" + sfSuffix, action: isSmartObj ? () => deps.smartMedian() : () => deps.promptMedian() },
              { label: "Reduce Noise…" + sfSuffix, action: () => deps.promptReduceNoise() },
            ]);
          }},
          { label: "Pixelate", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Color Halftone…" + sfSuffix, action: comingSoon("Color Halftone") },
              { label: "Crystallize…" + sfSuffix, action: () => deps.promptCrystallize() },
              { label: "Facet" + sfSuffix, action: () => deps.promptFacet() },
              { label: "Fragment" + sfSuffix, action: () => deps.promptFragment() },
              { label: "Mezzotint…" + sfSuffix, action: () => deps.promptMezzotint() },
              { label: "Mosaic…" + sfSuffix, action: isSmartObj ? () => deps.smartMosaic() : () => deps.promptMosaic() },
              { label: "Pointillize…" + sfSuffix, action: () => deps.promptPointillize() },
            ]);
          }},
          { label: "Render", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Flame…", action: comingSoon("Flame") },
              { label: "Picture Frame…", action: () => deps.promptPictureFrame() },
              { label: "Tree…", action: comingSoon("Tree") },
              { separator: true, label: "" },
              { label: "Clouds", action: () => deps.promptClouds() },
              { label: "Difference Clouds", action: () => deps.promptDiffClouds() },
              { label: "Fibers…", action: () => deps.promptFibers() },
              { label: "Lens Flare…", action: () => deps.promptLensFlare() },
              { label: "Lighting Effects…", action: () => deps.promptLightingEffects() },
            ]);
          }},
          { label: "Sharpen", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Shake Reduction…" + sfSuffix, action: comingSoon("Shake Reduction") },
              { label: "Sharpen" + sfSuffix, action: isSmartObj ? () => deps.smartSharpen() : () => deps.promptSharpen() },
              { label: "Sharpen Edges" + sfSuffix, action: comingSoon("Sharpen Edges") },
              { label: "Sharpen More" + sfSuffix, action: isSmartObj ? () => deps.smartSharpenMore() : () => deps.promptSharpenMore() },
              { label: "Smart Sharpen…" + sfSuffix, action: () => deps.promptSmartSharpen() },
              { label: "Unsharp Mask…" + sfSuffix, action: isSmartObj ? () => deps.smartUnsharpMask() : () => deps.promptUnsharpMask() },
            ]);
          }},
          { label: "Stylize", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Diffuse…" + sfSuffix, action: () => deps.promptDiffuse() },
              { label: "Emboss…" + sfSuffix, action: isSmartObj ? () => deps.smartEmboss() : () => deps.promptEmboss() },
              { label: "Extrude…" + sfSuffix, action: () => deps.promptExtrude() },
              { label: "Find Edges" + sfSuffix, action: isSmartObj ? () => deps.smartFindEdges() : () => deps.promptFindEdges() },
              { label: "Oil Paint…" + sfSuffix, action: () => deps.promptOilPaint() },
              { label: "Solarize" + sfSuffix, action: () => deps.promptSolarize() },
              { label: "Tiles…" + sfSuffix, action: () => deps.promptTiles() },
              { label: "Trace Contour…" + sfSuffix, action: () => deps.promptTraceContour() },
              { label: "Wind…" + sfSuffix, action: () => deps.promptWind() },
              { label: "Glowing Edges…" + sfSuffix, action: () => deps.promptGlowingEdges() },
            ]);
          }},
          { label: "Video", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "De-Interlace…", action: comingSoon("De-Interlace") },
              { label: "NTSC Colors", action: comingSoon("NTSC Colors") },
            ]);
          }},
          { label: "Other", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Custom…", action: comingSoon("Custom") },
              { label: "High Pass…" + sfSuffix, action: isSmartObj ? () => deps.smartHighPass() : () => deps.promptHighPass() },
              { label: "HSB/HSL…" + sfSuffix, action: () => deps.promptHSBHSL() },
              { label: "Maximum…" + sfSuffix, action: () => deps.promptMaximum() },
              { label: "Minimum…" + sfSuffix, action: () => deps.promptMinimum() },
              { label: "Offset…" + sfSuffix, action: () => deps.promptOffset() },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Browse Filters Online…", action: comingSoon("Browse Filters Online") },
        ]);
      });
    }

    if (li.textContent === "File") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        const recentFiles = deps.getRecentFiles();
        const hasDoc = !!deps.state.doc;
        // #61: anchor submenus to THIS menubar item, not the first li.
        const subAnchor = () => li;
        // #123: full Photoshop File menu including Browse in Bridge, Open As,
        // Open Recent, Close Others, Save a Copy, Save for Web (Legacy),
        // Export submenu, Generate, Share, Search, Automate, Scripts, Import,
        // Print One Copy and Exit. Items that depend on an open document are
        // disabled when state.doc is null.
        deps.showMenuDropdown(li, [
          { label: "New…", shortcut: "Ctrl+N", action: () => deps.newDocument() },
          { label: "Open…", shortcut: "Ctrl+O", action: () => deps.fileInput.click() },
          { label: "Browse in Bridge…", shortcut: "Alt+Ctrl+O", action: comingSoon("Browse in Bridge") },
          { label: "Open As…", shortcut: "Alt+Shift+Ctrl+O", action: () => deps.fileInput.click() },
          { label: "Open as Smart Object…", action: () => deps.openAsSmartObject() },
          { label: "Open from URL…", action: () => (deps as any).openFromURL?.() },
          { label: "Open Recent", disabled: recentFiles.length === 0, action: () => { if (recentFiles.length > 0) deps.showRecentFilesMenu(li, recentFiles); } },
          { separator: true, label: "" },
          { label: "Close", shortcut: "Ctrl+W", disabled: !hasDoc, action: () => deps.closeDoc() },
          { label: "Close All", shortcut: "Alt+Ctrl+W", disabled: !hasDoc, action: () => deps.closeAllDocs() },
          { label: "Close Others", shortcut: "Alt+Ctrl+P", disabled: !hasDoc, action: () => deps.closeAllDocs() },
          { label: "Close and Go to Bridge…", shortcut: "Shift+Ctrl+W", disabled: !hasDoc, action: comingSoon("Close and Go to Bridge") },
          { separator: true, label: "" },
          { label: "Save", shortcut: "Ctrl+S", disabled: !hasDoc, action: () => deps.showSaveAs() },
          { label: "Save As…", shortcut: "Ctrl+Shift+S", disabled: !hasDoc, action: () => deps.showSaveAs() },
          { label: "Save a Copy…", shortcut: "Alt+Ctrl+S", disabled: !hasDoc, action: () => deps.showSaveAs() },
          { label: "Revert", shortcut: "F12", disabled: !deps.state.savedPath, action: () => deps.revertDocument() },
          { separator: true, label: "" },
          { label: "Export", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Quick Export as PNG", shortcut: "Shift+Ctrl+'", disabled: !hasDoc, action: () => deps.quickExportPng() },
              { label: "Export As…", shortcut: "Alt+Shift+Ctrl+W", disabled: !hasDoc, action: () => deps.exportAs() },
              { label: "Export Preferences…", action: () => deps.promptPreferences() },
              { label: "Save for Web (Legacy)…", shortcut: "Alt+Shift+Ctrl+S", disabled: !hasDoc, action: () => deps.saveForWeb() },
              { separator: true, label: "" },
              { label: "Artboards to Files…", disabled: !hasDoc, action: () => deps.exportArtboardsToFiles() },
              { label: "Artboards to PDF…", disabled: !hasDoc, action: () => deps.exportArtboardsToPdf() },
              { label: "Color Lookup Tables…", disabled: !hasDoc, action: () => deps.exportColorLookupTables() },
              { label: "Data Sets as Files…", action: comingSoon("Data Sets as Files") },
              { label: "Layer Comps to Files…", action: comingSoon("Layer Comps to Files") },
              { label: "Layers to Files…", disabled: !hasDoc, action: () => deps.exportLayersToFiles() },
              { label: "Paths to Illustrator…", disabled: !hasDoc, action: () => deps.exportPathsToIllustrator() },
              { label: "Render Video…", action: comingSoon("Render Video") },
              { label: "Zoomify…", action: comingSoon("Zoomify") },
            ]);
          }},
          { label: "Generate", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Image Assets", action: comingSoon("Generate Image Assets") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Share…", action: comingSoon("Share") },
          { label: "Search", action: comingSoon("Search") },
          { separator: true, label: "" },
          { label: "Place Embedded…", disabled: !hasDoc, action: () => deps.placeEmbedded() },
          { label: "Place Linked…", disabled: !hasDoc, action: () => deps.placeLinked() },
          { label: "Package…", disabled: !hasDoc, action: comingSoon("Package") },
          { separator: true, label: "" },
          { label: "Automate", action: () => {
            // #129: Batch / Image Processor / Conditional Mode Change /
            // Create Droplet belong here, not in Window menu.
            deps.showMenuDropdown(subAnchor(), [
              { label: "Batch…", action: () => deps.showBatchDialog() },
              { label: "Image Processor…", action: () => deps.showImageProcessor() },
              { label: "Create Droplet…", action: () => deps.createDroplet() },
              { label: "Conditional Mode Change…", action: () => deps.conditionalModeChange() },
              { separator: true, label: "" },
              { label: "Contact Sheet II…", action: comingSoon("Contact Sheet II") },
              { label: "Crop and Straighten Photos", action: comingSoon("Crop and Straighten Photos") },
              { label: "Fit Image…", action: comingSoon("Fit Image") },
              { label: "Lens Correction…", action: () => deps.lensCorrection() },
              { label: "Merge to HDR Pro…", action: comingSoon("Merge to HDR Pro") },
              { label: "Photomerge…", action: comingSoon("Photomerge") },
            ]);
          }},
          { label: "Scripts", action: () => {
            // #129: Scripts belongs here under File, not in Window menu.
            deps.showMenuDropdown(subAnchor(), [
              { label: "Image Processor…", action: () => deps.showImageProcessor() },
              { label: "Delete All Empty Layers", action: comingSoon("Delete All Empty Layers") },
              { label: "Flatten All Layer Effects", action: comingSoon("Flatten All Layer Effects") },
              { label: "Flatten All Masks", action: comingSoon("Flatten All Masks") },
              { label: "Load Files into Stack…", action: comingSoon("Load Files into Stack") },
              { label: "Load Multiple DICOM Files…", action: comingSoon("Load Multiple DICOM Files") },
              { label: "Statistics…", action: comingSoon("Statistics") },
              { separator: true, label: "" },
              { label: "Browse…", action: () => deps.showScriptsDialog() },
              { label: "Event Manager…", action: comingSoon("Script Events Manager") },
              { label: "Edit Script", action: comingSoon("Edit Script") },
            ]);
          }},
          { label: "Import", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Variable Data Sets…", action: comingSoon("Variable Data Sets") },
              { label: "Video Frames to Layers…", action: comingSoon("Video Frames to Layers") },
              { label: "Notes…", action: comingSoon("Import Notes") },
              { label: "WIA Support…", action: comingSoon("WIA Support") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "File Info…", shortcut: "Alt+Shift+Ctrl+I", disabled: !hasDoc, action: () => deps.showFileInfo() },
          { separator: true, label: "" },
          { label: "Print…", shortcut: "Ctrl+P", disabled: !hasDoc, action: () => deps.showPrintDialog() },
          { label: "Print One Copy", shortcut: "Alt+Shift+Ctrl+P", disabled: !hasDoc, action: () => deps.showPrintDialog() },
          { separator: true, label: "" },
          { label: "Exit", shortcut: "Ctrl+Q", action: () => { window.close(); } },
        ]);
      });
    }

    if (li.textContent === "Edit") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        // #61: anchor submenus to THIS menubar item.
        const subAnchor = () => li;
        const hasDoc = !!deps.state.doc;
        // #124: "Transform" renamed to "Free Transform", Transform > submenu
        // added, Paste Special > submenu added, Purge > submenu added.
        // Fade / Search / Auto-Align / Auto-Blend / Puppet Warp / Perspective
        // Warp / Adobe PDF Presets / Preset Manager / Remote Connections /
        // Toolbar / Menus added.
        // #124 / audit i23: triggers Free Transform (Ctrl+T) or one of the
        // Transform > Scale/Rotate/Skew/Distort/Perspective submodes. Each
        // submenu item passes its own `mode`, which is set on the tool
        // BEFORE begin() so the very first drag already uses the right
        // gesture set (previously all five submenu items called this with
        // no mode argument, leaving `freeTransformTool.mode` stuck on
        // "free" forever — the sub-modes' applyDragSkew/Distort/Perspective
        // code paths were unreachable from the menu).
        const triggerFreeTransform = (mode: FreeTransformMode = "free") => {
          const ft = deps.freeTransformTool;
          ft.mode = mode;
          if (ft.begin(deps.toolCtx())) {
            deps.state.setActiveTool(ft);
            deps.highlightToolByShortcut("T");
          }
        };
        deps.showMenuDropdown(li, [
          { label: "Undo", shortcut: "Ctrl+Z", action: () => deps.state.undo() },
          { label: "Redo", shortcut: "Ctrl+Shift+Z", action: () => deps.state.redo() },
          { label: "Step Backward", shortcut: "Ctrl+Alt+Z", action: () => deps.state.stepBackward() },
          { label: "Step Forward", shortcut: "Ctrl+Shift+Z", action: () => deps.state.stepForward() },
          { label: "Fade…", shortcut: "Shift+Ctrl+F", action: () => (deps as any).fadeLastOperation?.() ?? comingSoon("Fade")() },
          { separator: true, label: "" },
          { label: "Cut", shortcut: "Ctrl+X", action: () => deps.cutSelection() },
          { label: "Copy", shortcut: "Ctrl+C", action: () => deps.copySelection() },
          { label: "Copy Merged", shortcut: "Shift+Ctrl+C", action: () => deps.copyMergedSelection() },
          { label: "Paste", shortcut: "Ctrl+V", action: () => deps.pasteClipboard() },
          { label: "Paste Special", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Paste Without Formatting", shortcut: "Shift+Ctrl+V", action: () => deps.pasteClipboard() },
              { label: "Paste In Place", shortcut: "Alt+Shift+Ctrl+V", action: () => deps.pasteInPlace() },
              { label: "Paste Into", shortcut: "Alt+Ctrl+V", action: () => deps.pasteInto() },
              { label: "Paste Outside", action: () => deps.pasteOutside() },
            ]);
          }},
          { label: "Clear", shortcut: "Delete", action: () => deps.clearSelection() },
          { separator: true, label: "" },
          { label: "Search", action: comingSoon("Search") },
          { label: "Check Spelling…", action: () => deps.checkSpelling() },
          { label: "Find and Replace Text…", action: () => deps.findAndReplaceText() },
          { separator: true, label: "" },
          { label: "Fill…", shortcut: "Shift+F5", disabled: !hasDoc, action: () => deps.promptFill() },
          { label: "Stroke…", disabled: !hasDoc, action: () => deps.promptStroke() },
          { label: "Content-Aware Fill…", disabled: !hasDoc, action: () => deps.promptContentAwareFill() },
          { label: "Content-Aware Scale", shortcut: "Alt+Shift+Ctrl+C", disabled: !hasDoc, action: comingSoon("Content-Aware Scale") },
          { label: "Puppet Warp", disabled: !hasDoc, action: comingSoon("Puppet Warp") },
          { label: "Perspective Warp", disabled: !hasDoc, action: comingSoon("Perspective Warp") },
          { separator: true, label: "" },
          { label: "Free Transform", shortcut: "Ctrl+T", disabled: !hasDoc, action: () => triggerFreeTransform("free") },
          { label: "Transform", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Again", shortcut: "Shift+Ctrl+T", action: () => deps.transformAgain() },
              { separator: true, label: "" },
              { label: "Scale", action: () => triggerFreeTransform("scale") },
              { label: "Rotate", action: () => triggerFreeTransform("rotate") },
              { label: "Skew", action: () => triggerFreeTransform("skew") },
              { label: "Distort", action: () => triggerFreeTransform("distort") },
              { label: "Perspective", action: () => triggerFreeTransform("perspective") },
              { label: "Warp", action: comingSoon("Transform: Warp") },
              { label: "Split Warp Horizontally", action: comingSoon("Split Warp Horizontally") },
              { label: "Split Warp Vertically", action: comingSoon("Split Warp Vertically") },
              { label: "Split Warp Crosswise", action: comingSoon("Split Warp Crosswise") },
              { label: "Remove Warp Split", action: comingSoon("Remove Warp Split") },
              { separator: true, label: "" },
              { label: "Convert to Smart Object", action: () => deps.convertToSmartObject() },
              { separator: true, label: "" },
              // audit i24: these five transform ONLY the active/selected
              // layer(s) via TransformLayerOrientationCommand. Image >
              // Image Rotation (below, a separate menu) still uses the
              // whole-document RotateImageCommand — unchanged by this fix.
              { label: "Rotate 180°", action: () => deps.transformLayerOrientation("180") },
              { label: "Rotate 90° Clockwise", action: () => deps.transformLayerOrientation("90-cw") },
              { label: "Rotate 90° Counter Clockwise", action: () => deps.transformLayerOrientation("90-ccw") },
              { label: "Flip Horizontal", action: () => deps.transformLayerOrientation("flip-h") },
              { label: "Flip Vertical", action: () => deps.transformLayerOrientation("flip-v") },
            ]);
          }},
          { label: "Auto-Align Layers…", action: comingSoon("Auto-Align Layers") },
          { label: "Auto-Blend Layers…", action: comingSoon("Auto-Blend Layers") },
          { label: "Sky Replacement…", action: comingSoon("Sky Replacement") },
          { separator: true, label: "" },
          { label: "Define Brush Preset…", action: () => deps.defineBrushPreset() },
          { label: "Define Pattern…", action: () => deps.definePattern() },
          { label: "Define Custom Shape…", action: comingSoon("Define Custom Shape") },
          { separator: true, label: "" },
          { label: "Purge", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Undo", action: () => deps.promptPurge() },
              { label: "Clipboard", action: () => deps.promptPurge() },
              { label: "Histories", action: () => deps.promptPurge() },
              { label: "All", action: () => deps.promptPurge() },
              { separator: true, label: "" },
              { label: "Video Cache", action: comingSoon("Purge: Video Cache") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Adobe PDF Presets…", action: comingSoon("Adobe PDF Presets") },
          { label: "Presets", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Preset Manager…", action: comingSoon("Preset Manager") },
              { label: "Export Presets…", action: comingSoon("Export Presets") },
              { label: "Import Presets…", action: comingSoon("Import Presets") },
              { label: "Migrate Presets…", action: comingSoon("Migrate Presets") },
            ]);
          }},
          { label: "Remote Connections…", action: comingSoon("Remote Connections") },
          { label: "Color Settings…", shortcut: "Shift+Ctrl+K", action: () => deps.promptColorSettings() },
          { label: "Assign Profile…", disabled: !hasDoc, action: comingSoon("Assign Profile") },
          { label: "Convert to Profile…", disabled: !hasDoc, action: comingSoon("Convert to Profile") },
          { separator: true, label: "" },
          { label: "Keyboard Shortcuts…", shortcut: "Alt+Shift+Ctrl+K", action: () => deps.openShortcutsEditor() },
          { label: "Menus…", shortcut: "Alt+Shift+Ctrl+M", action: comingSoon("Customize Menus") },
          { label: "Toolbar…", action: comingSoon("Customize Toolbar") },
          { label: "Preferences", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "General…", shortcut: "Ctrl+K", action: () => deps.promptPreferences() },
              { label: "Interface…", action: () => deps.promptPreferences() },
              { label: "Workspace…", action: () => deps.promptPreferences() },
              { label: "Tools…", action: () => deps.promptPreferences() },
              { label: "History Log…", action: () => deps.promptPreferences() },
              { label: "File Handling…", action: () => deps.promptPreferences() },
              { label: "Export…", action: () => deps.promptPreferences() },
              { label: "Performance…", action: () => deps.promptPreferences() },
              { label: "Scratch Disks…", action: () => deps.promptPreferences() },
              { label: "Cursors…", action: () => deps.promptPreferences() },
              { label: "Transparency & Gamut…", action: () => deps.promptPreferences() },
              { label: "Units & Rulers…", action: () => deps.promptPreferences() },
              { label: "Guides, Grid & Slices…", action: () => deps.promptPreferences() },
              { label: "Plug-ins…", action: () => deps.promptPreferences() },
              { label: "Type…", action: () => deps.promptPreferences() },
              { label: "3D…", action: () => deps.promptPreferences() },
              { label: "Technology Previews…", action: () => deps.promptPreferences() },
            ]);
          }},
        ]);
      });
    }

    if (li.textContent === "Image") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        // #61: anchor submenus to THIS menubar item, not the first li.
        // Previously this used `querySelector(".menubar__items li")` which
        // always returned the File item and made Image Rotation float over
        // the File menu position.
        const subAnchor = () => li;
        deps.showMenuDropdown(li, [
          { label: "Mode", action: () => {
            // i131: reflect the document's current color mode with a checkmark
            // (was a hardcoded "✓ RGB Color"); doc.colorMode is set by the
            // multi-layer ModeCommand.forDocument conversions.
            const cm = deps.state.doc?.colorMode ?? "rgb";
            const modeItem = (label: string, mode: string, action: () => void) => ({
              label: cm === mode ? `✓ ${label}` : label,
              action,
            });
            deps.showMenuDropdown(subAnchor(), [
              // RGB is the base mode; converting BACK to RGB isn't implemented,
              // so it stays disabled — only its checkmark is dynamic.
              { label: cm === "rgb" ? "✓ RGB Color" : "RGB Color", disabled: true },
              modeItem("CMYK Color", "cmyk", () => { (deps as any).convertToCmyk?.(); }),
              modeItem("Lab Color", "lab", () => { (deps as any).convertToLab?.(); }),
              modeItem("Grayscale", "grayscale", () => deps.convertToGrayscale()),
              modeItem("Indexed Color", "indexed", () => { (deps as any).convertToIndexed?.(); }),
              { label: "Multichannel", disabled: true },
              { label: "Duotone", disabled: true },
              { separator: true, label: "" },
              { label: "8 Bits/Channel", action: () => { (deps as any).convertTo8Bit?.(); } },
              { label: "16 Bits/Channel", action: () => { (deps as any).convertTo16Bit?.(); } },
              { label: "32 Bits/Channel", disabled: true },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Adjustments", action: () => deps.showImageAdjustmentsMenu() },
          { label: "Auto Tone", shortcut: "Shift+Ctrl+L", action: () => deps.promptAutoTone() },
          { label: "Auto Contrast", shortcut: "Alt+Shift+Ctrl+L", action: () => deps.promptAutoContrast() },
          { label: "Auto Color", shortcut: "Shift+Ctrl+B", action: () => deps.promptAutoColor() },
          { separator: true, label: "" },
          { label: "Image Size…", shortcut: "Ctrl+Alt+I", action: () => deps.promptImageSize() },
          { label: "Canvas Size…", shortcut: "Ctrl+Alt+C", action: () => deps.promptCanvasSize() },
          { label: "Image Rotation", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "180°", action: () => deps.rotateImage("180") },
              { label: "90° Clockwise", action: () => deps.rotateImage("90-cw") },
              { label: "90° Counter Clockwise", action: () => deps.rotateImage("90-ccw") },
              { separator: true, label: "" },
              { label: "Flip Horizontal", action: () => deps.rotateImage("flip-h") },
              { label: "Flip Vertical", action: () => deps.rotateImage("flip-v") },
              { separator: true, label: "" },
              { label: "Arbitrary…", action: () => deps.promptArbitraryRotation() },
            ]);
          }},
          { label: "Crop", action: () => {
            if (deps.state.activeTool === (deps as any).cropTool) { (deps as any).cropTool.commit((deps as any).toolCtx()); deps.state.requestRender(); }
            else { showTip("Activate the Crop tool first, then use Image > Crop to apply."); }
          }},
          { label: "Trim…", action: () => deps.trimImage() },
          { label: "Reveal All", action: () => deps.revealAll() },
          { separator: true, label: "" },
          { label: "Duplicate…", action: () => deps.duplicateDocument() },
          { label: "Apply Image…", action: () => deps.promptApplyImage() },
          { label: "Calculations…", action: () => deps.promptCalculations() },
          { separator: true, label: "" },
          { label: "Variables", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Define…", action: comingSoon("Variables: Define") },
              { label: "Data Sets…", action: comingSoon("Variables: Data Sets") },
            ]);
          }},
          { label: "Apply Data Set…", action: comingSoon("Apply Data Set") },
          { separator: true, label: "" },
          { label: "Trap…", action: comingSoon("Trap") },
          { label: "Analysis", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Set Measurement Scale", action: comingSoon("Set Measurement Scale") },
              { label: "Select Data Points", action: comingSoon("Select Data Points") },
              { label: "Record Measurements", action: comingSoon("Record Measurements") },
              { label: "Ruler Tool", action: comingSoon("Ruler Tool") },
              { label: "Count Tool", action: comingSoon("Count Tool") },
            ]);
          }},
        ]);
      });
    }

    if (li.textContent === "Layer") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        // #100: stop the event before it can leak to any document-level
        // handler that would otherwise fire `newDocument()` or similar.
        e.stopPropagation();
        e.preventDefault();
        const activeLayer = deps.state.doc && deps.state.doc.activeLayerId
          ? findLayer(deps.state.doc.layers, deps.state.doc.activeLayerId)
          : null;
        const canClip = activeLayer && !activeLayer.clippedToBelow && deps.state.doc && deps.state.doc.layers.length > 1;
        const canRelease = activeLayer?.clippedToBelow;
        // #61: anchor submenus to THIS menubar item.
        const subAnchor = () => li;
        const hasMask = activeLayer && activeLayer.kind === "raster" && (activeLayer as any).mask;
        const hasSelection = !!deps.state.doc?.selection;
        const hasVectorMask = activeLayer && activeLayer.kind === "shape" && (activeLayer as any).vectorMask;
        const hasStyles = activeLayer && (activeLayer as any).styles;
        // audit i2/i3: Align needs >=1 target (single-layer aligns to canvas);
        // Distribute needs >=3 (the two extremes stay put, only the "between"
        // layers move — with <3 there's nothing between them).
        const alignIds = deps.alignTargetIds?.() ?? [];
        const canAlign = alignIds.length >= 1;
        const canDistribute = alignIds.length >= 3;

        deps.showMenuDropdown(li, [
          { label: "New", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Layer…", shortcut: "Ctrl+Shift+N", action: () => deps.promptNewLayer() },
              { label: "Layer From Background", action: comingSoon("Layer From Background") },
              { label: "Group…", action: () => deps.groupActive() },
              { label: "Group from Layers", action: () => deps.groupFromLayers() },
              { label: "Artboard", action: () => deps.addNewArtboard() },
            ]);
          }},
          { label: "New Fill Layer", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Solid Color…", action: () => (deps as any).addSolidFillLayer?.() ?? deps.showFillLayerMenu() },
              { label: "Gradient…", action: () => (deps as any).addGradientFillLayer?.() ?? deps.showFillLayerMenu() },
              { label: "Pattern…", action: () => (deps as any).addPatternFillLayer?.() ?? deps.showFillLayerMenu() },
            ]);
          }},
          { label: "New Adjustment Layer", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Brightness/Contrast…", action: () => deps.promptBrightnessContrast() },
              { label: "Levels…", action: () => deps.promptLevels() },
              { label: "Curves…", action: () => deps.promptCurves() },
              { label: "Exposure…", action: () => deps.promptExposure() },
              { label: "Vibrance…", action: () => deps.promptVibrance() },
              { label: "Hue/Saturation…", action: () => deps.promptHueSaturation() },
              { label: "Color Balance…", action: () => deps.promptColorBalance() },
              { label: "Black & White…", action: () => deps.promptBlackWhite() },
              { label: "Photo Filter…", action: () => deps.promptPhotoFilter() },
              { label: "Channel Mixer…", action: () => deps.promptChannelMixer() },
              { label: "Color Lookup…", action: () => deps.promptColorLookup() },
              { label: "Invert", action: () => deps.promptInvert() },
              { label: "Posterize…", action: () => deps.promptPosterize() },
              { label: "Threshold…", action: () => deps.promptThreshold() },
              { label: "Gradient Map…", action: () => deps.promptGradientMap() },
              { label: "Selective Color…", action: () => deps.promptSelectiveColor() },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Duplicate Layer…", action: () => deps.promptDuplicateLayer() },
          { label: "Delete Layer", shortcut: "Backspace", action: () => deps.deleteActiveLayer() },
          { separator: true, label: "" },
          { label: "Layer Style", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Blending Options…", action: () => deps.showLayerStyleDialog() },
              { separator: true, label: "" },
              { label: "Drop Shadow…", action: () => deps.addLayerStyleEffect("dropShadow") },
              { label: "Inner Shadow…", action: () => deps.addLayerStyleEffect("innerShadow") },
              { label: "Outer Glow…", action: () => deps.addLayerStyleEffect("outerGlow") },
              { label: "Inner Glow…", action: () => deps.addLayerStyleEffect("innerGlow") },
              { label: "Bevel & Emboss…", action: () => deps.addLayerStyleEffect("bevelEmboss") },
              { label: "Satin…", action: () => deps.addLayerStyleEffect("satin") },
              { label: "Color Overlay…", action: () => deps.addLayerStyleEffect("colorOverlay") },
              { label: "Gradient Overlay…", action: () => deps.addLayerStyleEffect("gradientOverlay") },
              { label: "Pattern Overlay…", action: () => deps.addLayerStyleEffect("patternOverlay") },
              { label: "Stroke…", action: () => deps.addLayerStyleEffect("stroke") },
              { separator: true, label: "" },
              { label: "Copy Layer Style", disabled: !activeLayer || activeLayer.kind === "type" || !hasStyles, action: () => deps.copyLayerStyle() },
              { label: "Paste Layer Style", disabled: !(deps as any)._layerStyleClipboard, action: () => deps.pasteLayerStyle() },
              { label: "Clear Layer Style", disabled: !hasStyles, action: () => deps.clearLayerStyle() },
            ]);
          }},
          { label: "Smart Filter", disabled: true },
          { separator: true, label: "" },
          { label: "New Fill Content", disabled: true },
          { label: "New Adjustment Content", disabled: true },
          { label: "Layer Content Options…", disabled: true },
          { separator: true, label: "" },
          { label: "Layer Mask", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Reveal All", disabled: !deps.state.doc?.activeLayerId || hasMask, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).AddLayerMaskCommand(deps.state.doc.activeLayerId, "reveal-all"));
              }},
              { label: "Hide All", disabled: !deps.state.doc?.activeLayerId || hasMask, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).AddLayerMaskCommand(deps.state.doc.activeLayerId, "hide-all"));
              }},
              { label: "Reveal Selection", disabled: !deps.state.doc?.activeLayerId || hasMask || !hasSelection, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).AddLayerMaskCommand(deps.state.doc.activeLayerId, "from-selection"));
              }},
              { label: "Hide Selection", disabled: !deps.state.doc?.activeLayerId || hasMask || !hasSelection, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).AddLayerMaskCommand(deps.state.doc.activeLayerId, "hide-selection"));
              }},
              { label: "From Transparency", disabled: !deps.state.doc?.activeLayerId || hasMask, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).AddLayerMaskCommand(deps.state.doc.activeLayerId, "from-transparency"));
              }},
              { separator: true, label: "" },
              { label: "Delete", disabled: !hasMask, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).DeleteLayerMaskCommand(deps.state.doc.activeLayerId));
              }},
              { label: "Apply", disabled: !hasMask, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).ApplyLayerMaskCommand(deps.state.doc.activeLayerId));
              }},
              { separator: true, label: "" },
              { label: "Disable", disabled: !hasMask || !(activeLayer as any)?.mask?.enabled, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).EnableLayerMaskCommand(deps.state.doc.activeLayerId, false));
              }},
              { label: "Enable", disabled: !hasMask || (activeLayer as any)?.mask?.enabled, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).EnableLayerMaskCommand(deps.state.doc.activeLayerId, true));
              }},
            ]);
          }},
          { label: "Vector Mask", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Reveal All", disabled: !deps.state.doc?.activeLayerId || !hasVectorMask, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).SetLayerPropertyCommand(deps.state.doc.activeLayerId, { vectorMask: null }));
              }},
              { label: "Hide All", disabled: !deps.state.doc?.activeLayerId, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).SetLayerPropertyCommand(deps.state.doc.activeLayerId, { vectorMask: { subpaths: [] } }));
              }},
              { label: "Current Path", disabled: !deps.state.doc?.activeLayerId, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).SetLayerPropertyCommand(deps.state.doc.activeLayerId, { vectorMask: (deps as any).currentPathModel }));
              }},
              { separator: true, label: "" },
              { label: "Delete", disabled: !deps.state.doc?.activeLayerId || !hasVectorMask, action: () => {
                if (deps.state.doc?.activeLayerId) deps.state.execute(new (deps as any).SetLayerPropertyCommand(deps.state.doc.activeLayerId, { vectorMask: null }));
              }},
            ]);
          }},
          { separator: true, label: "" },
          { label: "Create Clipping Mask", shortcut: "Ctrl+Alt+G", disabled: !canClip, action: () => deps.createClippingMask() },
          { label: "Release Clipping Mask", shortcut: "Ctrl+Alt+Shift+G", disabled: !canRelease, action: () => deps.releaseClippingMask() },
          { separator: true, label: "" },
          { label: "Smart Objects", disabled: !deps.state.doc?.activeLayerId, action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Convert to Smart Object", disabled: !deps.state.doc?.activeLayerId, action: () => deps.convertToSmartObject() },
              { label: "Edit Contents", disabled: !deps.isSmartObjectActive(), action: () => deps.editSmartObject() },
              { label: "Replace Contents…", disabled: !deps.isSmartObjectActive(), action: () => deps.replaceSmartObjectContents() },
              { label: "Export Contents…", disabled: !deps.isSmartObjectActive(), action: () => deps.exportSmartObject() },
              { separator: true, label: "" },
              { label: "Rasterize Layer", disabled: !deps.isSmartObjectActive(), action: () => deps.rasterizeSmartObject() },
            ]);
          }},
          { label: "Rasterize", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Type", disabled: !activeLayer || activeLayer.kind !== "type", action: comingSoon("Rasterize Type") },
              { label: "Shape", disabled: !activeLayer || activeLayer.kind !== "shape", action: () => deps.rasterizeShapeLayer() },
              { label: "Fill Content", disabled: true, action: comingSoon("Rasterize Fill Content") },
              { label: "Vector Mask", disabled: !hasVectorMask, action: comingSoon("Rasterize Vector Mask") },
              { label: "Smart Object", disabled: !deps.isSmartObjectActive(), action: () => deps.rasterizeSmartObject() },
              { label: "Layer", disabled: !activeLayer, action: comingSoon("Rasterize Layer") },
              { label: "All Layers", action: comingSoon("Rasterize All Layers") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Layer via Copy", shortcut: "Ctrl+J", action: () => deps.layerViaCopy() },
          { label: "Layer via Cut", shortcut: "Ctrl+Shift+J", action: () => deps.layerViaCut() },
          { separator: true, label: "" },
          { label: "Group Layers", shortcut: "Ctrl+G", action: () => deps.groupActive() },
          { label: "Ungroup Layers", shortcut: "Ctrl+Shift+G", action: () => deps.ungroupActive() },
          { separator: true, label: "" },
          { label: "Hide Layers", action: () => deps.hideLayers() },
          { label: "Show All Layers", action: () => deps.showAllLayers() },
          { label: "Hide Others", action: () => deps.hideOthers() },
          { separator: true, label: "" },
          { label: "Arrange", action: () => {
            // i125: wired to the same reorderActiveLayer() the Ctrl+]/Ctrl+[
            // keyboard shortcuts already use (see main.ts) — previously every
            // item here was a comingSoon() no-op. "Reverse" has no
            // implementation to wire to yet, so it stays comingSoon().
            deps.showMenuDropdown(subAnchor(), [
              { label: "Bring to Front", shortcut: "Ctrl+Shift+]", disabled: !activeLayer, action: () => deps.reorderActiveLayer("front") },
              { label: "Bring Forward", shortcut: "Ctrl+]", disabled: !activeLayer, action: () => deps.reorderActiveLayer("forward") },
              { label: "Send Backward", shortcut: "Ctrl+[", disabled: !activeLayer, action: () => deps.reorderActiveLayer("backward") },
              { label: "Send to Back", shortcut: "Ctrl+Shift+[", disabled: !activeLayer, action: () => deps.reorderActiveLayer("back") },
              { separator: true, label: "" },
              { label: "Reverse", action: comingSoon("Reverse") },
            ]);
          }},
          { label: "Align", disabled: !canAlign, action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Top Edges", disabled: !canAlign, action: () => deps.alignLayers("top") },
              { label: "Vertical Centers", disabled: !canAlign, action: () => deps.alignLayers("v-center") },
              { label: "Bottom Edges", disabled: !canAlign, action: () => deps.alignLayers("bottom") },
              { label: "Left Edges", disabled: !canAlign, action: () => deps.alignLayers("left") },
              { label: "Horizontal Centers", disabled: !canAlign, action: () => deps.alignLayers("h-center") },
              { label: "Right Edges", disabled: !canAlign, action: () => deps.alignLayers("right") },
            ]);
          }},
          { label: "Distribute", disabled: !canDistribute, action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Top Edges", disabled: !canDistribute, action: () => deps.distributeLayers("top") },
              { label: "Vertical Centers", disabled: !canDistribute, action: () => deps.distributeLayers("v-center") },
              { label: "Bottom Edges", disabled: !canDistribute, action: () => deps.distributeLayers("bottom") },
              { label: "Left Edges", disabled: !canDistribute, action: () => deps.distributeLayers("left") },
              { label: "Horizontal Centers", disabled: !canDistribute, action: () => deps.distributeLayers("h-center") },
              { label: "Right Edges", disabled: !canDistribute, action: () => deps.distributeLayers("right") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Merge Down", shortcut: "Ctrl+E", action: () => deps.mergeDownActive() },
          { label: "Merge Layers", action: () => deps.mergeSelectedLayers() },
          { label: "Merge Visible", shortcut: "Ctrl+Shift+E", action: () => deps.state.execute(new (deps as any).MergeVisibleCommand()) },
          { label: "Flatten Image", action: () => deps.flattenImage() },
          { separator: true, label: "" },
          { label: "Matting", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Defringe…", action: comingSoon("Defringe") },
              { label: "Remove Black Matte", action: comingSoon("Remove Black Matte") },
              { label: "Remove White Matte", action: comingSoon("Remove White Matte") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Duplicate to New Document", action: () => deps.duplicateToNewDocument() },
          { label: "Export Layers to Files…", action: () => deps.exportLayersToFiles() },
          { label: "Export Artboards to Files…", action: () => deps.exportArtboardsToFiles() },
          { label: "Export Artboards to PDF…", action: () => deps.exportArtboardsToPdf() },
          { label: "Export Paths to Illustrator…", action: () => deps.exportPathsToIllustrator() },
          { label: "Export Color Lookup Tables…", action: () => deps.exportColorLookupTables() },
          { separator: true, label: "" },
          { label: "Blend If…", action: () => deps.promptBlendIf() },
          { label: "Layer Properties…", action: () => deps.layerProperties() },
          { label: "Blending Options…", action: () => deps.blendingOptions() },
          { label: "Scale Layer Effects…", action: () => deps.scaleLayerEffects() },
          { label: "Global Light…", action: () => deps.setGlobalLight() },
        ]);
      });
    }

    if (li.textContent === "Type") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        // #61: anchor submenus to THIS menubar item.
        const subAnchor = () => li;
        // i14: Warp Text… only makes sense against an active type layer.
        const activeLayer = deps.state.doc && deps.state.doc.activeLayerId
          ? findLayer(deps.state.doc.layers, deps.state.doc.activeLayerId)
          : null;
        const isActiveTypeLayer = !!activeLayer && activeLayer.kind === "type";
        const openPanelByName = (name: string) => () => {
          const tab = Array.from(document.querySelectorAll<HTMLButtonElement>(".panel-tab")).find((t) => t.textContent?.trim() === name);
          if (tab) tab.click(); else showTip(`${name} panel: Coming soon`);
        };
        deps.showMenuDropdown(li, [
          { label: "Panels", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Character", action: openPanelByName("Character") },
              { label: "Paragraph", action: openPanelByName("Paragraph") },
              { label: "Glyphs", action: comingSoon("Glyphs panel") },
              { label: "Character Styles", action: comingSoon("Character Styles panel") },
              { label: "Paragraph Styles", action: comingSoon("Paragraph Styles panel") },
            ]);
          }},
          { label: "Anti-Alias", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "None", action: comingSoon("Anti-Alias: None") },
              { label: "Sharp", action: comingSoon("Anti-Alias: Sharp") },
              { label: "Crisp", action: comingSoon("Anti-Alias: Crisp") },
              { label: "Strong", action: comingSoon("Anti-Alias: Strong") },
              { label: "Smooth", action: comingSoon("Anti-Alias: Smooth") },
            ]);
          }},
          { label: "Orientation", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Horizontal", action: comingSoon("Orientation: Horizontal") },
              { label: "Vertical", action: comingSoon("Orientation: Vertical") },
            ]);
          }},
          { label: "OpenType", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Standard Ligatures", action: comingSoon("Standard Ligatures") },
              { label: "Discretionary Ligatures", action: comingSoon("Discretionary Ligatures") },
              { label: "Fractions", action: comingSoon("Fractions") },
              { label: "Ordinals", action: comingSoon("Ordinals") },
              { label: "Stylistic Alternates", action: comingSoon("Stylistic Alternates") },
              { label: "Titling Alternates", action: comingSoon("Titling Alternates") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Extrude to 3D", action: comingSoon("Extrude to 3D") },
          { label: "Create Work Path", action: comingSoon("Create Work Path from Type") },
          { label: "Convert to Shape", action: comingSoon("Convert Type to Shape") },
          { label: "Rasterize Type Layer", action: comingSoon("Rasterize Type Layer") },
          { label: "Convert to Paragraph Text", action: comingSoon("Convert to Paragraph Text") },
          { label: "Convert to Point Text", action: comingSoon("Convert to Point Text") },
          { separator: true, label: "" },
          { label: "Warp Text…", disabled: !isActiveTypeLayer, action: () => deps.showWarpTextDialog() },
          { label: "Match Font", action: comingSoon("Match Font") },
          { separator: true, label: "" },
          { label: "Font Preview Size", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "None", action: comingSoon("Font Preview Size: None") },
              { label: "Small", action: comingSoon("Font Preview Size: Small") },
              { label: "Medium", action: comingSoon("Font Preview Size: Medium") },
              { label: "Large", action: comingSoon("Font Preview Size: Large") },
              { label: "Extra Large", action: comingSoon("Font Preview Size: Extra Large") },
              { label: "Huge", action: comingSoon("Font Preview Size: Huge") },
            ]);
          }},
          { label: "Language Options", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Default", action: comingSoon("Language Options: Default") },
              { label: "Middle Eastern Features", action: comingSoon("Middle Eastern Features") },
              { label: "East Asian Features", action: comingSoon("East Asian Features") },
            ]);
          }},
          { separator: true, label: "" },
          { label: "Update All Text Layers", action: comingSoon("Update All Text Layers") },
          { label: "Replace All Missing Fonts", action: comingSoon("Replace All Missing Fonts") },
          { label: "Resolve Missing Fonts", action: comingSoon("Resolve Missing Fonts") },
        ]);
      });
    }

    if (li.textContent === "3D") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        deps.showMenuDropdown(li, [
          { label: "New 3D Layer from File…", action: comingSoon("New 3D Layer from File") },
          { label: "New 3D Postcard from Layer", action: comingSoon("New 3D Postcard from Layer") },
          { label: "New 3D Extrusion from Selected Layer", action: comingSoon("New 3D Extrusion from Selected Layer") },
          { label: "New Mesh from Layer", action: comingSoon("New Mesh from Layer") },
          { separator: true, label: "" },
          { label: "Add Constraint(s) from…", action: comingSoon("Add Constraints") },
          { label: "Split Extrusion", action: comingSoon("Split Extrusion") },
          { label: "Merge 3D Layers", action: comingSoon("Merge 3D Layers") },
          { separator: true, label: "" },
          { label: "Export 3D Layer…", action: comingSoon("Export 3D Layer") },
          { label: "Render 3D Layer", action: comingSoon("Render 3D Layer") },
          { label: "Sketch With Current Brush", action: comingSoon("Sketch With Current Brush") },
          { separator: true, label: "" },
          { label: "Show / Hide Polygons", action: () => {
            // #61: anchor to the 3D menubar item itself.
            deps.showMenuDropdown(li, [
              { label: "Within Selection", action: comingSoon("Hide Within Selection") },
              { label: "Reveal All", action: comingSoon("Reveal All Polygons") },
            ]);
          }},
          { label: "Get More Content…", action: comingSoon("Get More 3D Content") },
        ]);
      });
    }

    if (li.textContent === "View") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        // #61: anchor submenus to THIS menubar item.
        const subAnchor = () => li;
        // #44 / #128: Screen Mode is a submenu, Snap To is a submenu, Show is
        // a submenu (Layer Edges / Selection Edges / Target Path / Grid /
        // Guides / Smart Guides / Pixel Grid / Slices / Notes / Mesh / Brush
        // Preview / Edit Pins / All / None). Proof Setup, Pixel Aspect Ratio,
        // and the multi-window Match submenu are also added (greyed if not
        // implemented). "Grid Settings…" removed (linked from Preferences).
        deps.showMenuDropdown(li, [
          { label: "Proof Setup", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Custom…", action: comingSoon("Proof Setup: Custom") },
              { separator: true, label: "" },
              { label: "Working CMYK", action: comingSoon("Proof Setup: Working CMYK") },
              { label: "Working Cyan Plate", action: comingSoon("Proof Setup: Working Cyan Plate") },
              { label: "Working Magenta Plate", action: comingSoon("Proof Setup: Working Magenta Plate") },
              { label: "Working Yellow Plate", action: comingSoon("Proof Setup: Working Yellow Plate") },
              { label: "Working Black Plate", action: comingSoon("Proof Setup: Working Black Plate") },
              { label: "Working CMY Plates", action: comingSoon("Proof Setup: Working CMY Plates") },
              { separator: true, label: "" },
              { label: "Legacy Macintosh RGB (Gamma 1.8)", action: comingSoon("Proof Setup: Legacy Macintosh RGB") },
              { label: "Internet Standard RGB (sRGB)", action: comingSoon("Proof Setup: sRGB") },
              { label: "Monitor RGB", action: comingSoon("Proof Setup: Monitor RGB") },
              { separator: true, label: "" },
              { label: "Color Blindness — Protanopia-type", action: comingSoon("Proof Setup: Protanopia") },
              { label: "Color Blindness — Deuteranopia-type", action: comingSoon("Proof Setup: Deuteranopia") },
            ]);
          }},
          { label: `${deps.proofColorsEnabled ? "✓ " : ""}Proof Colors`, shortcut: "Ctrl+Y", action: () => { deps.proofColorsEnabled = !deps.proofColorsEnabled; deps.gamutWarningEnabled = false; deps.state.requestRender(); } },
          { label: `${deps.gamutWarningEnabled ? "✓ " : ""}Gamut Warning`, shortcut: "Shift+Ctrl+Y", action: () => { deps.gamutWarningEnabled = !deps.gamutWarningEnabled; deps.proofColorsEnabled = false; deps.state.requestRender(); } },
          { separator: true, label: "" },
          { label: "Pixel Aspect Ratio", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "✓ Square", disabled: true },
              { label: "D1/DV NTSC (0.91)", action: comingSoon("Pixel Aspect Ratio: D1/DV NTSC") },
              { label: "D1/DV PAL (1.09)", action: comingSoon("Pixel Aspect Ratio: D1/DV PAL") },
              { label: "D1/DV NTSC Widescreen (1.21)", action: comingSoon("Pixel Aspect Ratio: NTSC Widescreen") },
              { label: "HDV 1080/DVCPRO HD 720 (1.33)", action: comingSoon("Pixel Aspect Ratio: HDV 1080") },
              { label: "D1/DV PAL Widescreen (1.46)", action: comingSoon("Pixel Aspect Ratio: PAL Widescreen") },
              { label: "Anamorphic 2:1 (2)", action: comingSoon("Pixel Aspect Ratio: Anamorphic 2:1") },
              { label: "DVCPRO HD 1080 (1.5)", action: comingSoon("Pixel Aspect Ratio: DVCPRO HD 1080") },
              { separator: true, label: "" },
              { label: "Custom Pixel Aspect Ratio…", action: comingSoon("Custom Pixel Aspect Ratio") },
              { label: "Delete Pixel Aspect Ratio…", action: comingSoon("Delete Pixel Aspect Ratio") },
              { label: "Reset Pixel Aspect Ratios…", action: comingSoon("Reset Pixel Aspect Ratios") },
            ]);
          }},
          { label: "Pixel Aspect Ratio Correction", action: comingSoon("Pixel Aspect Ratio Correction") },
          { label: "32-bit Preview Options…", action: comingSoon("32-bit Preview Options") },
          { separator: true, label: "" },
          { label: "Zoom In", shortcut: "Ctrl+=", action: () => deps.zoomCentered(2) },
          { label: "Zoom Out", shortcut: "Ctrl+-", action: () => deps.zoomCentered(0.5) },
          { label: "Fit on Screen", shortcut: "Ctrl+0", action: () => deps.fitDoc() },
          { label: "Fit Layer(s) on Screen", action: () => deps.fitDoc() },
          { label: "Fit Artboard on Screen", action: () => deps.fitDoc() },
          { label: "100%", shortcut: "Ctrl+1", action: () => deps.resetTo100() },
          { label: "Actual Pixels", action: () => deps.resetTo100() },
          { label: "200%", action: () => deps.zoomTo200() },
          { label: "Print Size", action: () => deps.viewPrintSize() },
          { separator: true, label: "" },
          { label: "Screen Mode", action: () => {
            // #128: Screen Mode rendered as a real submenu instead of three
            // flat items. F cycles between modes globally.
            deps.showMenuDropdown(subAnchor(), [
              { label: `${deps.screenMode === "standard" ? "✓ " : ""}Standard Screen Mode`, action: () => { deps.screenMode = "standard"; deps.cycleScreenMode(); } },
              { label: `${deps.screenMode === "full-menu" ? "✓ " : ""}Full Screen Mode With Menu Bar`, action: () => { deps.screenMode = "full-menu"; deps.cycleScreenMode(); } },
              { label: `${deps.screenMode === "full-screen" ? "✓ " : ""}Full Screen Mode`, action: () => { deps.screenMode = "full-screen"; deps.cycleScreenMode(); } },
            ]);
          }},
          { separator: true, label: "" },
          { label: `${deps.showExtras ? "✓ " : ""}Extras`, shortcut: "Ctrl+H", action: () => deps.toggleExtras() },
          { label: "Show", action: () => {
            // #44 / #128: full PS Show > submenu.
            deps.showMenuDropdown(subAnchor(), [
              { label: `${(deps as any).showLayerEdges ? "✓ " : ""}Layer Edges`, action: () => deps.toggleLayerEdges() },
              { label: `${deps.showSelectionEdges ? "✓ " : ""}Selection Edges`, action: () => deps.toggleSelectionEdges() },
              { label: `${deps.showTargetPath ? "✓ " : ""}Target Path`, shortcut: "Shift+Ctrl+H", action: () => deps.toggleTargetPath() },
              { separator: true, label: "" },
              { label: `${deps.showGrid ? "✓ " : ""}Grid`, shortcut: "Ctrl+'", action: () => deps.toggleGrid() },
              { label: `${deps.showGuides ? "✓ " : ""}Guides`, shortcut: "Ctrl+;", action: () => deps.toggleGuides() },
              { label: `${deps.showSmartGuides ? "✓ " : ""}Smart Guides`, action: () => { deps.showSmartGuides = !deps.showSmartGuides; deps.state.requestRender(); } },
              { label: `${(deps as any).showPixelGrid ? "✓ " : ""}Pixel Grid`, action: () => deps.togglePixelGrid() },
              { separator: true, label: "" },
              { label: `${(deps as any).showSlices ? "✓ " : ""}Slices`, action: () => deps.toggleSlices() },
              { label: `${deps.showNotes ? "✓ " : ""}Notes`, action: () => deps.toggleNotes() },
              { separator: true, label: "" },
              { label: "Mesh", action: comingSoon("Show: Mesh") },
              { label: "Brush Preview", action: comingSoon("Show: Brush Preview") },
              { label: "Edit Pins", action: comingSoon("Show: Edit Pins") },
              { separator: true, label: "" },
              { label: "All", action: () => { deps.showExtras = true; deps.state.requestRender(); } },
              { label: "None", action: () => { deps.showExtras = false; deps.state.requestRender(); } },
              { separator: true, label: "" },
              { label: "Show Extras Options…", action: comingSoon("Show Extras Options") },
            ]);
          }},
          { separator: true, label: "" },
          { label: `${deps.showRulers ? "✓ " : ""}Rulers`, shortcut: "Ctrl+R", action: () => deps.toggleRulers() },
          { separator: true, label: "" },
          { label: `${deps.snapEnabled ? "✓ " : ""}Snap`, shortcut: "Shift+Ctrl+;", action: () => deps.toggleSnap() },
          { label: "Snap To", action: () => {
            // #128: Snap To rendered as a real submenu.
            deps.showMenuDropdown(subAnchor(), [
              { label: `${deps.snapToGuides ? "✓ " : ""}Guides`, action: () => deps.toggleSnapToGuides() },
              { label: `${deps.snapToGrid ? "✓ " : ""}Grid`, action: () => deps.toggleSnapToGrid() },
              { label: `${deps.snapToLayers ? "✓ " : ""}Layers`, action: () => deps.toggleSnapToLayers() },
              { label: "Slices", action: comingSoon("Snap To: Slices") },
              { label: `${deps.snapToDocumentBounds ? "✓ " : ""}Document Bounds`, action: () => deps.toggleSnapToDocumentBounds() },
              { separator: true, label: "" },
              { label: "All", action: () => {
                if (!deps.snapToGuides) deps.toggleSnapToGuides();
                if (!deps.snapToGrid) deps.toggleSnapToGrid();
                if (!deps.snapToLayers) deps.toggleSnapToLayers();
                if (!deps.snapToDocumentBounds) deps.toggleSnapToDocumentBounds();
              }},
              { label: "None", action: () => {
                if (deps.snapToGuides) deps.toggleSnapToGuides();
                if (deps.snapToGrid) deps.toggleSnapToGrid();
                if (deps.snapToLayers) deps.toggleSnapToLayers();
                if (deps.snapToDocumentBounds) deps.toggleSnapToDocumentBounds();
              }},
            ]);
          }},
          { separator: true, label: "" },
          { label: `${(deps as any).patternPreview ? "✓ " : ""}Pattern Preview`, action: comingSoon("Pattern Preview") },
          { label: `${deps.guidesLocked ? "✓ " : ""}Lock Guides`, shortcut: "Alt+Ctrl+;", action: () => deps.toggleLockGuides() },
          { label: "Clear Guides", action: () => deps.clearGuides() },
          { label: "Clear Selected Artboard Guides", action: () => deps.clearGuides() },
          { label: "Clear Canvas Guides", action: () => deps.clearGuides() },
          { label: "Clear All Guides", action: () => deps.clearGuides() },
          { label: "New Guide…", action: () => deps.newGuide() },
          { label: "New Guide Layout…", action: () => deps.newGuideLayout() },
          { label: "New Guides From Shape", action: comingSoon("New Guides From Shape") },
          { separator: true, label: "" },
          { label: "Lock Slices", action: comingSoon("Lock Slices") },
          { label: "Clear Slices", action: comingSoon("Clear Slices") },
        ]);
      });
    }

    if (li.textContent === "Window") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        // #61: anchor submenus to THIS menubar item.
        const subAnchor = () => li;
        const toolrailEl = document.querySelector(".toolrail") as HTMLElement;
        const panelsEl = document.querySelector(".panels") as HTMLElement;
        const optionsbarEl = document.querySelector(".optionsbar") as HTMLElement;
        const statusbarEl = document.querySelector(".statusbar") as HTMLElement;
        const toolrailVisible = toolrailEl?.style.display !== "none";
        const panelsVisibleState = panelsEl?.style.display !== "none";
        const optionsbarVisible = optionsbarEl?.style.display !== "none";
        const statusbarVisible = statusbarEl?.style.display !== "none";
        const currentWorkspace = deps.getCurrentWorkspace();

        // #45: panel toggle helper. Click the named panel-tab to show /
        // activate its panel body, then mirror the activation status as a
        // ✓ in the menu label so the toggle is observable.
        const togglePanelByTabName = (name: string): boolean => {
          const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.panel-tab'));
          const tab = tabs.find((t) => t.textContent?.trim() === name);
          if (!tab) return false;
          tab.click();
          return tab.classList.contains("is-active");
        };
        const isPanelTabActive = (name: string): boolean => {
          const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.panel-tab'));
          const tab = tabs.find((t) => t.textContent?.trim() === name);
          return !!tab && tab.classList.contains("is-active");
        };

        // #129: Automate items (Batch, Image Processor, Conditional Mode
        // Change, Create Droplet, Scripts) moved OUT of Window into File >
        // Automate. Arrange and Workspace are real submenus.
        deps.showMenuDropdown(li, [
          { label: "Arrange", action: () => {
            // #45: Arrange submenu with Tile/Cascade/Float and Match items.
            deps.showMenuDropdown(subAnchor(), [
              { label: "Cascade", action: () => deps.cascadeDocuments() },
              { label: "Tile All Vertically", action: () => deps.tileDocuments() },
              { label: "Tile All Horizontally", action: () => deps.tileDocuments() },
              { label: "2-up Vertical", action: () => deps.tileDocuments() },
              { label: "2-up Horizontal", action: () => deps.tileDocuments() },
              { label: "3-up Vertical", action: () => deps.tileDocuments() },
              { label: "3-up Horizontal", action: () => deps.tileDocuments() },
              { label: "3-up Stacked", action: () => deps.tileDocuments() },
              { label: "4-up", action: () => deps.tileDocuments() },
              { label: "6-up", action: () => deps.tileDocuments() },
              { separator: true, label: "" },
              { label: "Consolidate All to Tabs", action: () => deps.consolidateTabs() },
              { separator: true, label: "" },
              { label: "Tile", action: () => deps.tileDocuments() },
              { label: "Float in Window", action: () => deps.floatDocument() },
              { label: "Float All in Windows", action: () => deps.floatDocument() },
              { separator: true, label: "" },
              { label: "Match Zoom", action: comingSoon("Match Zoom") },
              { label: "Match Location", action: comingSoon("Match Location") },
              { label: "Match Rotation", action: comingSoon("Match Rotation") },
              { label: "Match All", action: comingSoon("Match All") },
              { separator: true, label: "" },
              { label: `New Window for ${deps.state.doc?.name ?? "Document"}`, disabled: !deps.state.doc, action: comingSoon("New Window") },
              { label: "Minimize", shortcut: "Ctrl+M", action: comingSoon("Minimize Window") },
              { label: "Bring All to Front", action: comingSoon("Bring All to Front") },
            ]);
          }},
          { label: "Workspace", action: () => {
            // #45 / #129: Workspace submenu with named workspaces and
            // workspace-management items.
            type MenuEntry = { label: string; shortcut?: string; disabled?: boolean; action?: () => void; separator?: boolean };
            const wsItems: MenuEntry[] = ((deps.buildWorkspaceMenuItems(subAnchor()) || []) as any[]).map((it) => ({
              label: typeof it.label === "string" ? it.label : "",
              ...(typeof it.shortcut === "string" ? { shortcut: it.shortcut as string } : {}),
              ...(typeof it.disabled === "boolean" ? { disabled: it.disabled as boolean } : {}),
              ...(typeof it.action === "function" ? { action: it.action as () => void } : {}),
              ...(it.separator === true ? { separator: true as boolean } : {}),
            } as MenuEntry));
            const items: MenuEntry[] = [
              ...wsItems,
              { label: "", separator: true },
              { label: "Reset Essentials", action: comingSoon("Reset Workspace") },
              { label: "New Workspace…", action: comingSoon("New Workspace") },
              { label: "Delete Workspace…", action: comingSoon("Delete Workspace") },
              { label: "", separator: true },
              { label: "Keyboard Shortcuts & Menus…", action: () => deps.openShortcutsEditor() },
              { label: "Lock Workspace", action: comingSoon("Lock Workspace") },
            ];
            deps.showMenuDropdown(subAnchor(), items);
          }},
          { label: "Find Extensions on Exchange…", action: comingSoon("Find Extensions on Exchange") },
          { label: "Extensions (legacy)", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Manage Extensions…", action: comingSoon("Manage Extensions") },
            ]);
          }},
          { separator: true, label: "" },
          { label: `Workspace: ${currentWorkspace}`, disabled: true },
          { separator: true, label: "" },
          // Chrome / shell toggles (Pixelpa-specific, kept after Workspace).
          { label: `${toolrailVisible ? "✓ " : ""}Tools`, action: () => {
            if (toolrailEl) toolrailEl.style.display = toolrailVisible ? "none" : "";
            (deps as any).resize?.();
          }},
          { label: `${optionsbarVisible ? "✓ " : ""}Options`, action: () => {
            if (optionsbarEl) optionsbarEl.style.display = optionsbarVisible ? "none" : "";
            (deps as any).resize?.();
          }},
          { label: `${panelsVisibleState ? "✓ " : ""}Panels`, action: () => {
            if (panelsEl) panelsEl.style.display = panelsVisibleState ? "none" : "";
            (deps as any).resize?.();
          }},
          { label: `${statusbarVisible ? "✓ " : ""}Status Bar`, action: () => {
            if (statusbarEl) statusbarEl.style.display = statusbarVisible ? "none" : "";
            (deps as any).resize?.();
          }},
          { separator: true, label: "" },
          // #45: every panel item now actually shows/activates its panel.
          { label: "3D", action: comingSoon("3D panel") },
          { label: "Actions", shortcut: "Alt+F9", action: () => deps.showActionsPanel() },
          { label: "Adjustments", action: () => deps.openAdjustmentsPanel() },
          { label: "Brushes", shortcut: "F5", action: () => deps.openBrushSettingsPanel() },
          { label: "Brush Settings", action: () => deps.openBrushSettingsPanel() },
          { label: `${isPanelTabActive("Channels") ? "✓ " : ""}Channels`, action: () => togglePanelByTabName("Channels") },
          { label: `${isPanelTabActive("Character") ? "✓ " : ""}Character`, action: () => togglePanelByTabName("Character") },
          { label: "Character Styles", action: comingSoon("Character Styles panel") },
          // i84: was comingSoon("Clone Source panel"); opens the real (if
          // still-stub, per CloneSourcePanel.ts) floating panel now.
          { label: "Clone Source", action: () => deps.openCloneSourcePanel() },
          // i128: was referencing `(deps as any).ColorPanel` /
          // `(deps as any).colorPanelInstance`, neither of which
          // buildMenuDeps() ever set — `new (deps as any).ColorPanel()`
          // threw (ColorPanel undefined), making this a de-facto no-op.
          { label: "Color", shortcut: "F6", action: () => deps.openColorPanel() },
          { label: "Glyphs", action: comingSoon("Glyphs panel") },
          { label: "Gradients", action: () => deps.openGradientEditor() },
          { label: "Gradient Editor", action: () => deps.openGradientEditor() },
          { label: `${isPanelTabActive("History") ? "✓ " : ""}History`, action: () => {
            togglePanelByTabName("History");
            (deps as any).renderHistoryPanel?.();
          }},
          { label: "Histogram", action: () => deps.openHistogramPanel() },
          { label: "Info", shortcut: "F8", action: () => deps.openInfoPanel() },
          { label: `${isPanelTabActive("Layers") ? "✓ " : ""}Layers`, shortcut: "F7", action: () => togglePanelByTabName("Layers") },
          { label: "Layer Comps", action: () => deps.showLayerCompsPanel() },
          { label: "Learn", action: comingSoon("Learn panel") },
          { label: "Libraries", action: comingSoon("Libraries panel") },
          { label: "Measurement Log", action: comingSoon("Measurement Log") },
          { label: "Modifier Keys", action: comingSoon("Modifier Keys panel") },
          { label: "Navigator", action: () => deps.openNavigatorPanel() },
          { label: "Notes", action: () => deps.toggleNotes() },
          { label: `${isPanelTabActive("Paragraph") ? "✓ " : ""}Paragraph`, action: () => togglePanelByTabName("Paragraph") },
          { label: "Paragraph Styles", action: comingSoon("Paragraph Styles panel") },
          { label: `${isPanelTabActive("Paths") ? "✓ " : ""}Paths`, action: () => {
            togglePanelByTabName("Paths");
            (deps as any).syncPathsPanel?.();
          }},
          { label: "Patterns", action: () => deps.openPatternEditor() },
          { label: "Pattern Editor", action: () => deps.openPatternEditor() },
          { label: "Properties", action: () => deps.openPropertiesPanel() },
          { label: "Shapes", action: () => deps.openShapesPanel() },
          { label: "Styles", action: () => deps.openStylesPanel() },
          { label: "Swatches", action: () => deps.openSwatchesPanel() },
          { label: "Timeline", action: comingSoon("Timeline panel") },
          { label: "Tool Presets", action: comingSoon("Tool Presets panel") },
          { separator: true, label: "" },
          { label: "Options Bar", action: () => {
            if (optionsbarEl) optionsbarEl.style.display = optionsbarVisible ? "none" : "";
            (deps as any).resize?.();
          }},
          { label: "Tools (Toolbar)", action: () => {
            if (toolrailEl) toolrailEl.style.display = toolrailVisible ? "none" : "";
            (deps as any).resize?.();
          }},
        ]);
      });
    }

    if (li.textContent === "Help") {
      li.style.cursor = "pointer";
      li.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        // #61: anchor submenus to THIS menubar item.
        const subAnchor = () => li;
        // #132: "Keyboard Shortcuts…" removed (already lives in Edit menu).
        // Photoshop Help (F1), Photoshop Tutorials, About Plug-ins, Manage
        // Extensions, Legal Notices, System Info, Updates, Plug-ins submenu
        // and About Pixelpa added.
        deps.showMenuDropdown(li, [
          { label: "Pixelpa Help", shortcut: "F1", action: () => deps.openTutorials() },
          { label: "Pixelpa Tutorials", action: () => deps.openTutorials() },
          { separator: true, label: "" },
          { label: "What's New…", action: () => deps.showWhatsNew() },
          { label: "Updates…", action: comingSoon("Updates") },
          { separator: true, label: "" },
          { label: "Plug-ins", action: () => {
            deps.showMenuDropdown(subAnchor(), [
              { label: "Browse Plug-ins…", action: comingSoon("Browse Plug-ins") },
              { label: "Plug-ins Folder…", action: comingSoon("Plug-ins Folder") },
              { label: "Reload Plug-ins", action: comingSoon("Reload Plug-ins") },
            ]);
          }},
          { label: "About Plug-ins", action: comingSoon("About Plug-ins") },
          { label: "Manage Extensions…", action: comingSoon("Manage Extensions") },
          { separator: true, label: "" },
          { label: "System Info…", action: comingSoon("System Info") },
          { label: "Legal Notices", action: comingSoon("Legal Notices") },
          { separator: true, label: "" },
          { label: "About Pixelpa…", action: () => deps.showAbout() },
        ]);
      });
    }
  });
}
